import React from 'react';
import {useTranslation} from 'next-i18next';
import {NavLink} from '@tempo/nextjs';

// Components
import Thumbnail from '@components/Thumbnail';
import RankProgress from '@components/RankProgress';

import {useLocalizedFormat} from '@deezer/next/contexts/localization';
import {SimpleGrid, Text, VStack, HStack} from '@tempo/core';
import AnalyticCard from '@deezer/next/features/Analytic/components/AnalyticCard';
import type {TopTrack} from '@deezer/next/services/api/Legacy/requests/analytic/types';
import Placeholder from '@components/Placeholder';

interface TopTracksProps {
	topTracks?: TopTrack[];
	isLoading: boolean;
	isError: boolean;
}

type TrackItemProps = {
	track: TopTrack;
};

const TrackItem = ({track}: TrackItemProps) => {
	const {t} = useTranslation('d4c');
	const {formatNumber} = useLocalizedFormat();

	const artistsLinks: React.ReactNode[] = [];

	if (track.artists && track.artists.length) {
		track.artists.forEach((artist, artistIndex) => {
			artistsLinks.push(
				artistIndex > 0 ? ', ' : '',
				artist.redirectUrl ? (
					<NavLink href={artist.redirectUrl} key={artist.id}>
						{artist.title}
					</NavLink>
				) : (
					<span key={artist.id}>{artist.title}</span>
				),
			);
		});
	}

	return (
		<NavLink
			href={track.redirectUrl}
			position="relative"
			py={{base: 'spacing.m', md: 'spacing.l'}}
			ps={{base: 'spacing.l', md: 'spacing.xl'}}
			_hover={{
				bg: 'background.neutral.primary.hovered',
			}}
			// Styling for grid dividers is based on https://geary.co/internal-borders-css-grid/
			sx={{
				'&::before, &::after': {
					content: "''",
					position: 'absolute',
					backgroundColor: 'divider.neutral.primary.default',
					zIndex: 1,
				},
				'&::after': {
					inlineSize: '100%',
					blockSize: '1px',
					insetInlineStart: 0,
					insetBlockEnd: '-1px',
				},
				'&::before': {
					inlineSize: '1px',
					blockSize: '100%',
					insetBlockStart: 0,
					insetInlineEnd: '-1px',
				},
			}}
		>
			<HStack spacing={{base: 'spacing.m', md: 'spacing.xl'}}>
				<Text variant="caption.regular" color="text.neutral.primary.default">
					{String(track.rank).padStart(2, '0')}
				</Text>
				<HStack pe="spacing.s" spacing={{base: 'spacing.s', md: 'spacing.m'}}>
					<Thumbnail
						picture={track.picture}
						height={{base: '32px', lg: '64px'}}
						width={{base: '32px', lg: '64px'}}
						rounded={false}
						type={track.type}
					/>
					<VStack alignItems="initial" spacing={0}>
						<Text variant="caption.strong" noOfLines={1}>
							{track.title}
						</Text>
						<Text
							variant="caption.regular"
							noOfLines={1}
							color="text.neutral.secondary.default"
						>
							{track.artists[0]?.title}
						</Text>
						<Text
							variant="caption.regular"
							color="text.neutral.secondary.default"
						>
							{t('analyticshome_subtitle_streams_d4c-web')}{' '}
							<strong>{track.total ? formatNumber(track.total) : '-'}</strong>
						</Text>
						<RankProgress
							progress={track.progress}
							justifyContent="flex-start"
						/>
					</VStack>
				</HStack>
			</HStack>
		</NavLink>
	);
};

const TopTracks = ({topTracks, isLoading, isError}: TopTracksProps) => {
	const {t} = useTranslation('d4c');

	return (
		<AnalyticCard
			hasNoData={!topTracks || !topTracks.length}
			title={t('analyticshome_title_moststreamedtracks_d4c-web')}
			data-testid="top-tracks"
			isLoading={isLoading}
			isError={isError}
			placeholder={<Placeholder />}
			variant="overview"
		>
			<SimpleGrid columns={{base: 2, md: 4, xl: 5}} overflow="hidden">
				{topTracks?.map(item => (
					<TrackItem key={item.id} track={item} />
				))}
			</SimpleGrid>
		</AnalyticCard>
	);
};

export default TopTracks;
